import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { CompositeFilterDescriptor, GroupDescriptor, process, SortDescriptor, } from '@progress/kendo-data-query';
import {
    ColumnReorderEvent,
    FilterableSettings,
    GridComponent,
    PageChangeEvent,
    PagerSettings,
    SelectableSettings,
    SortSettings
} from '@progress/kendo-angular-grid';
import { ActionColumnService } from '../../services/action-column.service';
import { ActionComponent } from '../action/action.component';
import { Column } from '../../classes/column';
import { DataTableStateService } from '../../services/data-table-state.service';
import { State } from '../../interfaces/state';
import { ValueService } from '../../services/value.service';
import { StateChangeEvent } from '@kaiser-software/chassis-datatable/src/classes/state-change-event';
import { Data } from '../../interfaces/data';

// Load all required data for the NL locale
// import '@progress/kendo-angular-intl/locales/nl/all';

@Component({
    selector: 'chassis-datatable-2',
    templateUrl: './datatable.component.html',
    styleUrls: ['./datatable.component.scss'],
    // providers: [{provide: LOCALE_ID, useValue: 'nl'}]
    providers: [
        DataTableStateService,
        ActionColumnService,
    ]
})
export class DatatableComponent implements OnInit, OnChanges, AfterViewInit {

    /**
     * Should the data be processed locally by the data table.
     */
    @Input() public process = true;

    /**
     * The page number for the pagination.
     * Use of `page` will override `skip`.
     */
    @Input() public page = 1;

    /**
     * The number of records to skip.
     */
    @Input() public skip = 0;

    /**
     * The number of records to take.
     */
    @Input() public take = 10;

    /**
     * The list of columns.
     */
    @Input() public columns: Array<Column> = [];

    @Input() public data: Data = {
        data: [],
        total: 0,
    };

    /**
     * The view is the `data` which is displayed in the grid.
     */
    public view: Data = {
        data: [],
        total: 0,
    };

    /**
     * The hidden column names.
     */
    @Input() public hidden: Array<string> = [];

    /**
     * The selected column keys.
     */
    public selected: Array<number> = [];

    /**
     * The list of row actions.
     */
    @Input() public actions: Array<string> = [];

    @Input() public selectable: SelectableSettings = {
        enabled: true,
        checkboxOnly: true,
        mode: 'multiple'
    };

    @Input() public sortable: SortSettings = {
        allowUnsort: true,
        mode: 'single',
    };

    @Input() public pageable: PagerSettings = {
        buttonCount: 5,
        info: true,
        type: 'numeric',
        pageSizes: true,
        previousNext: true
    };

    @Input() public filterable: FilterableSettings = 'menu';

    @Input() public reorderable = false;

    @Input() public pageSizeOptions: Array<number> = [10, 25, 50, 100];

    /**
     * The data table change event emitter.
     * Use an event name different than change to prevent event bubbling.
     */
    @Output('changed') public change: EventEmitter<StateChangeEvent> = new EventEmitter();

    public state: State;

    /**
     * The underlying grid component instance.
     */
    @ViewChild(GridComponent) protected component: GridComponent;

    /**
     * The list of action components.
     */
    @ViewChildren(ActionComponent) protected actionComponents: QueryList<ActionComponent>;

    /**
     * The calculated width of the actions column.
     * The default value has to be greater than 0 to avoid column
     * width error.
     */
    protected actionsColumnWidth = 100;

    constructor(
        public valueService: ValueService,
        protected changeDetectorRef: ChangeDetectorRef,
        protected stateService: DataTableStateService,
        protected actionColumnService: ActionColumnService,
    ) {
        // Listen to the state changes so there is at least an initial
        // state on component initialization.
        this.listenToStateChange();
    }

    public ngOnInit(): void {
        this.emitChangeEvent();

        this.initPagination();
    }

    /**
     * Listen to state changes and sync the state.
     */
    protected listenToStateChange(): void {
        this.stateService.state
            .subscribe((state) => {
                this.state = state;

                if (this.process) {
                    // Process the data at each state change for local data
                    // processing.
                    this.processData(this.data);
                }
            });
    }

    /**
     * Emit the change event on state changes.
     */
    protected emitChangeEvent(): void {
        this.stateService.changes
            .subscribe((event: StateChangeEvent) => {
                this.change.emit(event);
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.updateStateOnChange(changes);

        this.processDataOnChange(changes);
    }

    protected updateStateOnChange(changes: SimpleChanges): void {
        this.updateColumnsOnChange(changes);
        this.updateSkipOnChange(changes);
        this.updateTakeOnChange(changes);
        this.updateHiddenOnChange(changes);
    }

    protected updateColumnsOnChange(changes: SimpleChanges): void {
        if (changes.columns) {
            this.stateService.setColumns(
                changes.columns.currentValue,
                changes.columns.isFirstChange(),
            );
        }
    }

    protected updateSkipOnChange(changes: SimpleChanges): void {
        if (changes.skip) {
            this.stateService.setSkip(
                changes.skip.currentValue,
                changes.skip.isFirstChange(),
            );
        }
    }

    protected updateTakeOnChange(changes: SimpleChanges): void {
        if (changes.take) {
            this.stateService.setTake(
                changes.take.currentValue,
                changes.take.isFirstChange(),
            );
        }
    }

    protected processDataOnChange(changes: SimpleChanges): void {
        if (changes.data) {
            this.processData(
                changes.data.currentValue
            );
        }
    }

    protected updateHiddenOnChange(changes: SimpleChanges): void {
        if (changes.hidden) {
            this.stateService.setColumns(
                this.applyHiddenToColumns(
                    changes.hidden.currentValue
                ),
                changes.hidden.isFirstChange(),
            );
        }
    }

    protected applyHiddenToColumns(columns: Array<string>): Array<Column> {
        return this.state.columns
            .map((column) => {
                if (columns.includes(column.name)) {
                    column.hidden = true
                }

                return column;
            });
    }

    /**
     * Initialize the pagination.
     */
    protected initPagination(): void {
        // Set up initial offset and initial data process
        // for local data processing.
        if (this.process) {
            this.stateService.setSkip(
                (this.page - 1) * this.take
            );

            this.processData(this.data);
        }
    }

    public ngAfterViewInit(): void {
        this.resizeActionsColumn();
    }

    /**
     * Resize the actions column width.
     */
    protected resizeActionsColumn(): void {
        // this.actionsColumnWidth = this.actionColumnService.calculateWidth(
        //     this.actionComponents.toArray()
        // );

        this.changeDetectorRef.detectChanges();
    }

    /**
     * Set the data table items and data.
     *
     * @param data
     */
    protected processData<T>(data: Data): void {
        this.view = this.process
            ? process(data.data, this.state)
            : data;
    }

    /**
     * Update the pagination on page change.
     *
     * @param skip
     * @param take
     */
    protected onPageChange({skip, take}: PageChangeEvent): void {
        this.stateService.setSkip(skip);
        this.stateService.setTake(take);

        this.processData(this.data);
    }

    /**
     * Executes when the grid state sort changes.
     *
     * @param sort
     */
    public onSortChange(sort: Array<SortDescriptor>): void {
        this.stateService.setSort(sort);

        // Clear the selected fields.
        this.selected = [];
    }

    /**
     * Executes when the grid state group changes.
     * Todo: Implement
     *
     * @param group
     */
    public onGroupChange(group: Array<GroupDescriptor>): void {
        //
    }

    /**
     * Executes when the grid state filter changes.
     *
     * @param filter
     */
    public onFilterChange(filter: CompositeFilterDescriptor): void {
        this.stateService.setFilter(filter);

        // Go to the first page in case the filter results will not
        // be on the current page.
        this.stateService.setSkip(0);

        // Clear the selected fields.
        this.selected = [];
    }

    /**
     * Executes when a grid row is selected.
     * Todo: Implement
     *
     * @param event
     */
    public onSelectionChange(keys): void {
        //
    }

    /**
     * Executes when the column visibility on the grid changes.
     *
     * @param changed
     */
    public onColumnVisibilityChange(changed: Array<Column>): void {
        const columns = Array.from(this.state.columns)
            .map((column) => {
                const found = changed.find((c) => {
                    return c.field === column.attribute
                });

                if (found) {
                    column.hidden = found.hidden;
                }

                return column;
            });

        // Update the state columns.
        this.stateService.setColumns(columns);
    }

    /**
     * Executes when the grid column order changes.
     * Todo: Implement
     *
     * @param event
     */
    public onColumnOrderChange(event: ColumnReorderEvent): void {
        //
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-datatable/src/components/datatable/datatable.component.ts